import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Navigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../components/ui/NotificationProvider';

const getLocalValue = key => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const formatCop = value => {
  const number = Number(value);

  if (!Number.isFinite(number)) return '—';

  return new Intl.NumberFormat(
    'es-CO',
    {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0
    }
  ).format(number);
};

const formatUsd = value => {
  const number = Number(value);

  if (!Number.isFinite(number)) return '—';

  return new Intl.NumberFormat(
    'en-US',
    {
      style: 'currency',
      currency: 'USD'
    }
  ).format(number);
};

const getPriceSyncId = result => String(
  result?.item?.ml_item_id ||
  result?.ml_item_id ||
  result?.item?.id ||
  ''
);

const getPriceSyncStock = result => {
  const amazon = result?.amazon || result?.details?.amazon || {};
  const code = result?.details?.amazon?.errorCode || amazon?.errorCode;

  if (
    code === 'AMAZON_OUT_OF_STOCK' ||
    amazon?.availability === 'out_of_stock'
  ) return 'out_of_stock';

  if (amazon?.availability === 'in_stock') return 'in_stock';

  return 'unknown';
};

const knownPriceCategoryNames = new Map();
const requestedPriceCategoryNames = new Set();


// Selector flotante que no desplaza la tabla.
const MultiCategoryPicker = ({ label, options, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [position, setPosition] = React.useState({
    top: 0, left: 0, width: 300, maxHeight: 300
  });

  const trigger = React.useRef(null);
  const popup = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const reposition = () => {
      if (!trigger.current) return;

      const rect = trigger.current.getBoundingClientRect();

      const width = Math.min(
        380,
        Math.max(280, rect.width),
        window.innerWidth - 16
      );

      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const placeAbove = below < 230 && above > below;

      const maxHeight = Math.max(
        130,
        Math.min(320, placeAbove ? above : below)
      );

      setPosition({
        width,
        maxHeight,
        left: Math.max(
          8,
          Math.min(rect.left, window.innerWidth - width - 8)
        ),
        top: placeAbove
          ? Math.max(8, rect.top - maxHeight - 4)
          : rect.bottom + 4
      });
    };

    const closeOutside = event => {
      if (
        !trigger.current?.contains(event.target) &&
        !popup.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    reposition();

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    document.addEventListener('pointerdown', closeOutside);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  const matches = options.filter(opt =>
    `${opt.id} ${opt.name}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase())
  );

  const selectedLabel = value.length === 0
    ? 'Todas las categorías'
    : `${value.length} categorías seleccionadas`;

  return <>
    <button
      ref={trigger}
      type="button"
      aria-label={label}
      aria-expanded={open}
      onClick={() => setOpen(v => !v)}
      className="inline-flex w-[300px] max-w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm text-text-primary"
    >
      <span className="truncate">{selectedLabel}</span>
      <span className="shrink-0" aria-hidden="true">▾</span>
    </button>

    {open && createPortal(
      <div
        ref={popup}
        className="z-[9999] overflow-hidden rounded-lg border border-border bg-background shadow-xl"
        style={{position: 'fixed', ...position}}
      >
        <div className="border-b border-border p-2">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar nombre o código MCO"
            aria-label={`Buscar ${label}`}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />

          <button
            type="button"
            onClick={() => onChange([])}
            className="mt-2 rounded px-2 py-1 text-sm text-primary hover:bg-muted"
          >
            Todas las categorías
          </button>
        </div>

        <div
          className="overflow-y-auto p-2"
          style={{
            maxHeight: Math.max(70, position.maxHeight - 105)
          }}
        >
          {matches.map(opt => (
            <label
              key={opt.id}
              className="flex cursor-pointer items-start gap-2 rounded px-2 py-2 text-sm hover:bg-muted"
            >
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={value.includes(opt.id)}
                onChange={e =>
                  onChange(old =>
                    e.target.checked
                      ? [...new Set([...old, opt.id])]
                      : old.filter(id => id !== opt.id)
                  )
                }
              />

              <span className="min-w-0 break-words">
                {opt.name}
              </span>
            </label>
          ))}

          {matches.length === 0 && (
            <p className="p-2 text-sm text-text-secondary">
              Sin coincidencias
            </p>
          )}
        </div>
      </div>,
      document.body
    )}
  </>;
};

// Paginador independiente para cada tabla.
const ResultPager = ({
  page, total, size, onPage, onSize
}) => {
  const pages = Math.max(1, Math.ceil(total / size));
  const start = total ? (page - 1) * size + 1 : 0;
  const end = Math.min(page * size, total);

  return (
    <div className="my-3 flex flex-wrap items-center justify-between gap-2 text-sm text-text-secondary">
      <span>Mostrando {start}–{end} de {total}</span>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2">
          Por página

          <select
            value={size}
            onChange={e => onSize(Number(e.target.value))}
            className="w-24 min-w-[96px] rounded-md border border-border bg-background pl-3 pr-9 py-1 text-sm"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>

        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded border border-border px-3 py-1 disabled:opacity-40"
        >
          Anterior
        </button>

        <span>Página {page} de {pages}</span>

        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="rounded border border-border px-3 py-1 disabled:opacity-40"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

const PriceUpdater = () => {
  const [priceFilter, setPriceFilter] = useState('changes');
  const [priceSearch, setPriceSearch] = useState('');
  const [priceCategoryFilter, setPriceCategoryFilter] = useState([]);
  const [priceCategoryNames, setPriceCategoryNames] = useState(
    () => Object.fromEntries(knownPriceCategoryNames)
  );
  const [priceStockFilter, setPriceStockFilter] = useState('all');
  const [selectedPriceIds, setSelectedPriceIds] = useState([]);
  const [amazonPauseSection, setAmazonPauseSection] = useState('prices');
  const [amazonPauseTabCategory, setAmazonPauseTabCategory] = useState([]);
  const [amazonPauseTabSearch, setAmazonPauseTabSearch] = useState('');
  const [pricePage, setPricePage] = useState(1);
  const [pricePageSize, setPricePageSize] = useState(25);
  const [amazonPausePage, setAmazonPausePage] = useState(1);
  const [amazonPausePageSize, setAmazonPausePageSize] = useState(25);

  useEffect(() => setPricePage(1), [
    priceFilter, priceSearch, priceCategoryFilter, priceStockFilter
  ]);

  useEffect(() => setAmazonPausePage(1), [
    amazonPauseTabSearch, amazonPauseTabCategory
  ]);
  const [selectedAmazonPauseIds, setSelectedAmazonPauseIds] = useState([]);
  const [pausedPriceItems, setPausedPriceItems] = useState([]);
  const [selectedPausedIds, setSelectedPausedIds] = useState([]);
  const [showPaused, setShowPaused] = useState(false);
  const [loadingPaused, setLoadingPaused] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [pausedSearch, setPausedSearch] = useState('');
  const [pausedCategory, setPausedCategory] = useState('all');

  const {
    user,
    userProfile
  } = useAuth();

    const {
      showSuccess,
      showError,
      showWarning,
      showConfirmation
    } = useNotification();

  const [items, setItems] =
    useState([]);

  const [activeTotal, setActiveTotal] =
    useState(null);

  const [results, setResults] =
    useState([]);

  const [loadingItems, setLoadingItems] =
    useState(true);

  const [scanning, setScanning] =
    useState(false);

    const [applying, setApplying] =
      useState(false);

  const [progress, setProgress] =
    useState(0);

  const [discoverySummary, setDiscoverySummary] =
    useState(null);

  const [restoredSellerId, setRestoredSellerId] =
    useState(null);

  const [error, setError] =
    useState('');

  const sellerId =
    getLocalValue(
      'mercadolibre_user_id'
    );

  const mercadoLibreAccessToken =
    getLocalValue(
      'mercadolibre_access_token'
    );

  const hasAccess =
    userProfile?.role === 'admin' ||
    user?.app_metadata?.permissions
      ?.price_updater === true;

  useEffect(() => {
    if (!sellerId) {
      setResults([]);
      setSelectedPriceIds([]);
      setDiscoverySummary(null);
      setProgress(0);
      setRestoredSellerId(null);
      return;
    }
    const key = `mercadobulk_price_updater_${sellerId}`;
    try {
      const raw = window.localStorage.getItem(key);
      const saved = raw ? JSON.parse(raw) : null;
      setResults(Array.isArray(saved?.results) ? saved.results : []);
      setDiscoverySummary(saved?.discoverySummary || null);
      setProgress(Number(saved?.progress) || 0);
    } catch {
      setResults([]);
      setSelectedPriceIds([]);
      setDiscoverySummary(null);
      setProgress(0);
    }
    setRestoredSellerId(sellerId);
  }, [sellerId]);

  useEffect(() => {
    if (!sellerId || restoredSellerId !== sellerId) return;
    try {
      window.localStorage.setItem(
        `mercadobulk_price_updater_${sellerId}`,
        JSON.stringify({ results, discoverySummary, progress })
      );
    } catch {}
  }, [sellerId, restoredSellerId, results, discoverySummary, progress]);

  const requestPriceSync =
    useCallback(
      async (
        url,
        options = {}
      ) => {
        const {
          data: { session }
        } =
          await supabase.auth
            .getSession();

        if (!session?.access_token) {
          const authError =
            new Error(
              'Tu sesión de Mercado Bulk expiró.'
            );

          authError.status = 401;
          throw authError;
        }

        if (
          !sellerId ||
          !mercadoLibreAccessToken
        ) {
          const sellerError =
            new Error(
              'Este perfil no tiene una conexión válida de Mercado Libre.'
            );

          sellerError.status = 409;
          throw sellerError;
        }

        const response =
          await fetch(
            url,
            {
              ...options,
              cache: 'no-store',
              headers: {
                'Content-Type':
                  'application/json',
                Authorization:
                  `Bearer ${session.access_token}`,
                'X-MercadoLibre-User-Id':
                  sellerId,
                'X-MercadoLibre-Access-Token':
                  mercadoLibreAccessToken,
                ...(options.headers || {})
              }
            }
          );

        const payload =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          payload?.success === false
        ) {
          const requestError =
            new Error(
              payload?.message ||
              `Error HTTP ${response.status}`
            );

          requestError.status =
            response.status;

          requestError.payload =
            payload;

          throw requestError;
        }

        return payload;
      },
      [
        sellerId,
        mercadoLibreAccessToken
      ]
    );

  const loadItems =
    useCallback(
      async () => {
        setLoadingItems(true);
        setError('');

        try {
          const payload =
            await requestPriceSync(
              '/api/price-sync/items'
            );

          setItems(
            Array.isArray(payload?.data)
              ? payload.data
              : []
          );


            const parsedActiveTotal =
              Number(payload?.active_total);

            setActiveTotal(
              Number.isFinite(parsedActiveTotal)
                ? parsedActiveTotal
                : null
            );
        } catch (err) {
          setItems([]);
            setActiveTotal(null);

          setError(
            err?.message ||
            'No fue posible cargar los productos.'
          );
        } finally {
          setLoadingItems(false);
        }
      },
      [requestPriceSync]
    );

  useEffect(() => {
    if (!userProfile || !hasAccess) {
      return;
    }

    loadItems();
  }, [
    userProfile,
    hasAccess,
    loadItems
  ]);

    const handleScan =
      async () => {
        if (scanning) {
          return;
        }

        setScanning(true);
        setProgress(0);
        setResults([]);
        setSelectedPriceIds([]);
        setError('');
        setDiscoverySummary(null);

        showWarning(
          activeTotal !== null
            ? `Comenzó la revisión de ${activeTotal} publicaciones del seller ${sellerId}.`
            : `Comenzó la revisión del seller ${sellerId}.`,
          {
            title: 'Búsqueda de precios iniciada'
          }
        );

        try {
          /*
           * PRIMERA FASE:
           * descubrir las publicaciones del seller REAL
           * conectado en este navegador.
           *
           * Funciona aunque items.length sea 0.
           */
          const started =
            await requestPriceSync(
              '/api/price-sync/discover',
              {
                method: 'POST',
                body:
                  JSON.stringify({})
              }
            );

          const jobId =
            Number(
              started?.job_id
            );

          if (
            !Number.isInteger(jobId) ||
            jobId <= 0
          ) {
            throw new Error(
              'El servidor no creó el trabajo de revisión.'
            );
          }

          let job = null;

          /*
           * El trabajo corre en el VPS.
           * La pantalla solamente consulta progreso.
           */
          for (
            let attempt = 0;
            attempt < 900;
            attempt += 1
          ) {
            const statusPayload =
              await requestPriceSync(
                `/api/price-sync/discover/${jobId}`
              );

            job =
              statusPayload?.data ||
              null;

            if (job) {
              setDiscoverySummary(job);

              setProgress(
                Number(
                  job.scanned_items
                ) || 0
              );
            }

            if (
              job?.status ===
              'completed'
            ) {
              break;
            }

            if (
              job?.status ===
              'failed'
            ) {
              throw new Error(
                job?.error_text ||
                'La revisión del seller falló.'
              );
            }

            await new Promise(
              resolve =>
                setTimeout(
                  resolve,
                  2000
                )
            );
          }

          if (
            !job ||
            job.status !== 'completed'
          ) {
            throw new Error(
              'La revisión tardó demasiado y no terminó.'
            );
          }

          /*
           * Recargar únicamente los productos que
           * quedaron con ASIN + contexto suficiente.
           */
          const freshPayload =
            await requestPriceSync(
              '/api/price-sync/items'
            );

          const freshItems =
            Array.isArray(
              freshPayload?.data
            )
              ? freshPayload.data
              : [];

          setItems(
            freshItems
          );

          if (!freshItems.length) {
            showWarning(
              `Se revisaron ${Number(job.total_items || 0)} publicaciones. ` +
              `${Number(job.asin_found || 0)} tenían un ASIN demostrable, ` +
              `pero todavía no hay publicaciones con todos los datos necesarios para recalcular precio.`,
              {
                title:
                  'Revisión del seller terminada'
              }
            );

            return;
          }

          /*
           * SEGUNDA FASE:
           * ejecutar el preview seguro existente
           * únicamente sobre los vínculos válidos.
           *
           * Aquí todavía NO se modifica ningún precio.
           */
          const collected = [];

          for (
            let index = 0;
            index < freshItems.length;
            index += 1
          ) {
            const item =
              freshItems[index];

            try {
              const payload =
                await requestPriceSync(
                  `/api/price-sync/items/${encodeURIComponent(
                    item.ml_item_id
                  )}/preview`,
                  {
                    method: 'POST',
                    body:
                      JSON.stringify({})
                  }
                );

              const mergedItem = {
                ...item,
                ...(payload?.item || {}),

                id:
                  payload?.item?.id ||
                  item?.ml_item_id ||
                  null,

                ml_item_id:
                  payload?.item?.id ||
                  item?.ml_item_id ||
                  null,

                asin:
                  payload?.amazon?.asin ||
                  item?.asin ||
                  null
              };

              collected.push({
                ...payload,
                ok: true,
                item:
                  mergedItem,
                ml_item_id:
                  mergedItem.ml_item_id,
                asin:
                  mergedItem.asin
              });

            } catch (err) {
              if (err?.payload?.stage === "item_status" && err?.payload?.status !== "active") continue;
              collected.push({
                item: {
                  ...item,
                  ...(err?.payload?.item || {})
                },
                ml_item_id:
                  item?.ml_item_id ||
                  null,
                asin:
                  item?.asin ||
                  null,
                ok: false,
                error:
                  err?.message ||
                  'No fue posible revisar este producto.',
                details:
                  err?.payload ||
                  null
              });
            }

            setResults(
              [...collected]
            );
          }

          await loadItems();
          setResults([...collected]);

          const changed =
            collected.filter(
              result =>
                result?.ok &&
                result?.wouldUpdate === true
            ).length;

          const review =
            collected.filter(
              result =>
                !result?.ok
            ).length;

          setDiscoverySummary(prev => ({
            ...(prev || {}),
            ready_items: collected.length,
            asin_found: collected.length,
            missing_context: review
          }));

          setDiscoverySummary(prev => ({
            ...(prev || {}),
            ready_items: collected.length,
            asin_found: collected.length,
            missing_context: review
          }));

          showSuccess(
            `Seller revisado: ${Number(job.total_items || 0)} publicaciones activas, ` +
            `${collected.length} listas para comprobar precio, ` +
            `${changed} cambio${changed === 1 ? '' : 's'} encontrado${changed === 1 ? '' : 's'}` +
            `${review ? ` y ${review} requieren revisión.` : '.'}`,
            {
              title:
                'Búsqueda de precios terminada'
            }
          );

        } catch (err) {
          const message =
            err?.message ||
            'No fue posible revisar los precios.';

          setError(message);

          showError(
            message,
            {
              title:
                'Error en Actualizador de precios'
            }
          );

        } finally {
          setScanning(false);
        }
      };

  const changedCount =
    useMemo(
      () =>
        results.filter(
          result =>
            result.ok &&
            result.wouldUpdate === true
        ).length,
      [results]
    );

  const reviewCount =
    useMemo(
      () =>
        results.filter(
          result =>
            !result.ok
        ).length,
      [results]
    );

  const unchangedCount =
    useMemo(
      () =>
        results.filter(
          result =>
            result.ok &&
            result.wouldUpdate !== true
        ).length,
      [results]
    );

    const executePriceUpdates =
      async (approvedIds = []) => {
        const approvedSet = new Set(approvedIds.map(String));
        const pending =
          results.filter(
            result =>
              result?.ok === true &&
              result?.wouldUpdate === true &&
              approvedSet.has(getPriceSyncId(result))
          );

        if (!pending.length) {
          showWarning(
            'No hay cambios de precio pendientes.',
            {
              title: 'Sin cambios'
            }
          );
          return;
        }

        setApplying(true);
        setError('');

        const nextResults =
          [...results];

        let updated = 0;
        let failed = 0;

        try {
          for (
            let index = 0;
            index < nextResults.length;
            index += 1
          ) {
            const result =
              nextResults[index];

            if (
              result?.ok !== true ||
              result?.wouldUpdate !== true ||
              !approvedSet.has(getPriceSyncId(result))
            ) {
              continue;
            }

            const itemId =
              result?.item?.id ||
              result?.item?.ml_item_id ||
              result?.ml_item_id;

            if (!itemId) {
              failed += 1;

              nextResults[index] = {
                ...result,
                applyError:
                  'La publicación no tiene ID de Mercado Libre.'
              };

              setResults(
                [...nextResults]
              );

              continue;
            }

            try {
              const payload =
                await requestPriceSync(
                  `/api/price-sync/items/${encodeURIComponent(
                    itemId
                  )}/apply`,
                  {
                    method: 'POST',
                    body:
                      JSON.stringify({})
                  }
                );

              const appliedPrice =
                Number(
                  payload?.mercadoLibre
                    ?.appliedPriceCop ??
                  result?.mercadoLibre
                    ?.proposedPriceCop
                );

              nextResults[index] = {
                ...result,

                ok: true,
                applied:
                  payload?.updated === true,

                wouldUpdate: false,

                mercadoLibre: {
                  ...(result?.mercadoLibre || {}),

                  currentPriceCop:
                    Number.isFinite(appliedPrice)
                      ? appliedPrice
                      : result?.mercadoLibre
                          ?.currentPriceCop,

                  proposedPriceCop:
                    Number.isFinite(appliedPrice)
                      ? appliedPrice
                      : result?.mercadoLibre
                          ?.proposedPriceCop,

                  differenceCop: 0
                },

                applyResult:
                  payload
              };

              if (
                payload?.updated === true
              ) {
                updated += 1;
              }

            } catch (err) {
              failed += 1;

              nextResults[index] = {
                ...result,

                applyError:
                  err?.message ||
                  'No fue posible actualizar este precio.'
              };
            }

            setResults(
              [...nextResults]
            );
          }

          await loadItems();

          if (updated > 0) {
            showSuccess(
              `${updated} precio${
                updated === 1 ? '' : 's'
              } actualizado${
                updated === 1 ? '' : 's'
              } y verificado${
                updated === 1 ? '' : 's'
              } en Mercado Libre.`,
              {
                title:
                  'Actualización completada'
              }
            );
          }

          if (failed > 0) {
            showError(
              `${failed} publicación${
                failed === 1 ? '' : 'es'
              } no pudo${
                failed === 1 ? '' : 'ieron'
              } actualizarse. Revisa el estado mostrado en la tabla.`,
              {
                title:
                  'Actualización incompleta'
              }
            );
          }

        } finally {
          setApplying(false);
        }
      };

    const handlePriceUpdates =
      () => {
        const selectedSet = new Set(selectedPriceIds);
        const pending =
          filteredPriceResults.filter(
            result =>
              result?.ok === true &&
              result?.wouldUpdate === true &&
              selectedSet.has(getPriceSyncId(result))
          );

        if (!pending.length) {
          showWarning(
            'Selecciona las publicaciones con cambios de precio que quieras actualizar.',
            {
              title:
                'Sin cambios pendientes'
            }
          );
          return;
        }

        showConfirmation({
          title:
            'Actualizar precios',

          message:
            `Se actualizarán ${pending.length} publicación${
              pending.length === 1
                ? ''
                : 'es'
            } del seller ${sellerId}. Antes de cada PUT el servidor recalculará Amazon y validará nuevamente el seller.`,

          confirmText:
            'Actualizar precios',

          cancelText:
            'Cancelar',

          type:
            'warning',

          onConfirm: () => executePriceUpdates(pending.map(getPriceSyncId))
        });
      };



    const priceCategories = Array.from(
      new Map(
        results.map(result => {
          const id = String(result?.item?.category_id || '').trim();
          const name = String(
            priceCategoryNames[id] ||
            result?.item?.category_name ||
            ''
          ).trim();
          return [id, name ? `${id} — ${name}` : id];
        }).filter(([id]) => Boolean(id))
      ),
      ([id, name]) => ({ id, name })
    ).sort((a, b) => a.name.localeCompare(b.name));

    const filteredPriceResults = results.filter(result => {
      if (result?.pausedStatus === 'paused') return false;
      const matchesStatus =
        priceFilter === 'all' ||
        (priceFilter === 'changes' && result?.ok === true && result?.wouldUpdate === true) ||
        (priceFilter === 'unchanged' && result?.ok === true && result?.wouldUpdate !== true) ||
        (priceFilter === 'review' && result?.ok !== true);

      const text = [
        result?.item?.title,
        result?.item?.asin,
        result?.asin,
        result?.item?.ml_item_id
      ].filter(Boolean).join(' ').toLowerCase();

      const categoryId =
        String(result?.item?.category_id || '').trim();

      const matchesCategory =
        priceCategoryFilter.length === 0 ||
        priceCategoryFilter.includes(categoryId);

      const matchesStock =
        priceStockFilter === 'all' ||
        getPriceSyncStock(result) === priceStockFilter;

      return matchesStatus &&
        matchesCategory &&
        matchesStock &&
        text.includes(priceSearch.trim().toLowerCase());
    });

    useEffect(() => {
      const categoryIds = [...new Set(
        results.map(result =>
          String(result?.item?.category_id || '').trim()
        )
      )].filter(id =>
        /^MCO\d+$/.test(id) &&
        !requestedPriceCategoryNames.has(id)
      );

      if (!categoryIds.length) return;

      categoryIds.forEach(id =>
        requestedPriceCategoryNames.add(id)
      );

      const resolveNames = async () => {
        for (
          let index = 0;
          index < categoryIds.length;
          index += 4
        ) {
          await Promise.all(
            categoryIds.slice(index, index + 4).map(async id => {
              try {
                const data = await requestPriceSync(
                  `/api/price-sync/category-name/${encodeURIComponent(id)}`
                );

                const name =
                  String(data?.name || '').trim();

                if (name) {
                  knownPriceCategoryNames.set(id, name);

                  setPriceCategoryNames(previous => ({
                    ...previous,
                    [id]: name
                  }));
                }
              } catch {
                // Mantener MCO cuando el nombre no esté disponible.
              }
            })
          );
        }
      };

      resolveNames();

    }, [results, requestPriceSync]);

    const pricePages = Math.max(
      1, Math.ceil(filteredPriceResults.length / pricePageSize)
    );

    const visiblePricePage = Math.min(pricePage, pricePages);

    const pagedPriceResults = filteredPriceResults.slice(
      (visiblePricePage - 1) * pricePageSize,
      visiblePricePage * pricePageSize
    );

    const selectedVisibleResults =
      filteredPriceResults.filter(result =>
        selectedPriceIds.includes(
          getPriceSyncId(result)
        )
      );

    const selectedApplicableCount =
      selectedVisibleResults.filter(result =>
        result?.ok === true &&
        result?.wouldUpdate === true
      ).length;


    const getPausedItems = async () => {
      setLoadingPaused(true);
      try {
        const response = await requestPriceSync('/api/price-sync/paused-items');
        if (!Array.isArray(response?.items)) throw new Error('Respuesta inesperada');
        setPausedPriceItems(response.items);
        setSelectedPausedIds([]);
      } catch (e) {
        showError(e?.message || 'No se pudieron consultar las pausadas.');
      } finally {
        setLoadingPaused(false);
      }
    };

    const amazonUnavailableCandidates = results.filter(r => {
      const errorCode =
        r?.details?.amazon?.errorCode ||
        r?.amazon?.errorCode ||
        r?.details?.errorCode;

      const explicitlyOutOfStock =
        errorCode === 'AMAZON_OUT_OF_STOCK' ||
        r?.amazon?.availability === 'out_of_stock' ||
        r?.details?.availability === 'out_of_stock';

      return Boolean(getPriceSyncId(r)) &&
        r?.pausedStatus !== 'paused' &&
        explicitlyOutOfStock;
    });

    const amazonPauseCategories = [...new Set(
      amazonUnavailableCandidates.map(r =>
        String(r?.item?.category_id || '').trim()
      ).filter(Boolean)
    )].sort();

    const amazonPauseTabResults =
      amazonUnavailableCandidates.filter(r => {
        const category =
          String(r?.item?.category_id || '').trim();

        const text = [
          r?.item?.title,
          r?.item?.asin,
          r?.asin,
          getPriceSyncId(r)
        ].filter(Boolean).join(' ').toLowerCase();

        return (
          amazonPauseTabCategory.length === 0 ||
          amazonPauseTabCategory.includes(category)
        ) && text.includes(
          amazonPauseTabSearch.trim().toLowerCase()
        );
      });

    const amazonPausePages = Math.max(
      1,
      Math.ceil(amazonPauseTabResults.length / amazonPausePageSize)
    );

    const visibleAmazonPausePage = Math.min(
      amazonPausePage, amazonPausePages
    );

    const pagedAmazonPauseResults = amazonPauseTabResults.slice(
      (visibleAmazonPausePage - 1) * amazonPausePageSize,
      visibleAmazonPausePage * amazonPausePageSize
    );

    const pauseTargets = amazonPauseTabResults.filter(r =>
      selectedAmazonPauseIds.includes(getPriceSyncId(r))
    );

    const pausedVisible = pausedPriceItems.filter(r =>
      Array.isArray(r.sub_status) &&
      r.sub_status.length === 1 &&
      r.sub_status[0] === 'paused_by_seller' &&
      (pausedCategory === 'all' || r.category_id === pausedCategory) &&
      [r.title, r.asin, r.ml_item_id]
        .filter(Boolean).join(' ').toLowerCase()
        .includes(pausedSearch.toLowerCase())
    );

    const canResume = r =>
      Array.isArray(r.sub_status) &&
      r.sub_status.includes('paused_by_seller');

    const resumeTargets = pausedVisible.filter(r =>
      selectedPausedIds.includes(String(r.ml_item_id)) && canResume(r)
    );

    const pausedCategories = [
      ...new Set(pausedPriceItems.map(r => r.category_id).filter(Boolean))
    ].sort();

    const changeSelectedStatus = async (ids, target) => {
      if (!ids.length || changingStatus || applying || scanning) return;

      setChangingStatus(true);
      const done = [];
      const errors = [];

      try {
        for (const id of ids) {
          try {
            const response = await requestPriceSync(
              `/api/price-sync/items/${encodeURIComponent(id)}/status`,
              {
                method: 'POST',
                body: JSON.stringify({status: target})
              }
            );

            if (response?.success && response?.status === target) {
              done.push(String(id));
            } else {
              errors.push(String(id));
            }
          } catch (e) {
            errors.push(`${id}: ${e?.message || 'Error'}`);
          }
        }

        if (done.length) {
          const idsDone = new Set(done);

          if (target === 'paused') {
            setResults(old => old.map(r =>
              idsDone.has(getPriceSyncId(r))
                ? {...r, pausedStatus: 'paused', wouldUpdate: false}
                : r
            ));
            setSelectedAmazonPauseIds(old =>
              old.filter(id => !idsDone.has(id))
            );
          } else {
            setSelectedPausedIds(old =>
              old.filter(id => !idsDone.has(id))
            );
          }

          await loadItems();

          if (showPaused || target === 'active') {
            await getPausedItems();
          }

          showSuccess(
            `${done.length} publicación(es) ${
              target === 'paused' ? 'pausada(s)' : 'reactivada(s)'
            }.`
          );
        }

        if (errors.length) {
          showError(
            `${errors.length} sin completar: ${errors.slice(0, 2).join(' | ')}`
          );
        }
      } finally {
        setChangingStatus(false);
      }
    };

    const confirmPricePause = () => {
      const ids = pauseTargets.map(getPriceSyncId);
      if (!ids.length) return;

      showConfirmation({
        title: 'Pausar publicaciones',
        message: `Se pausarán solamente ${ids.length} publicaciones seleccionadas del vendedor ${sellerId}. ¿Confirmas?`,
        confirmText: `Pausar ${ids.length}`,
        cancelText: 'Cancelar',
        type: 'warning',
        onConfirm: () => changeSelectedStatus(ids, 'paused')
      });
    };

    const confirmPriceResume = () => {
      const ids = resumeTargets.map(r => String(r.ml_item_id));
      if (!ids.length) return;

      showConfirmation({
        title: 'Reactivar publicaciones',
        message: `Se intentarán reactivar ${ids.length} publicaciones. Verifica que estén disponibles para la venta. ¿Confirmas?`,
        confirmText: `Reactivar ${ids.length}`,
        cancelText: 'Cancelar',
        type: 'warning',
        onConfirm: () => changeSelectedStatus(ids, 'active')
      });
    };

    const summaryCards = [
      {
        label:
          'Publicaciones activas',
        value:
          loadingItems
            ? '…'
            : (
                activeTotal !== null
                  ? activeTotal
                  : '—'
              ),
        icon: 'Package'
      },
      {
        label:
          'Con ASIN listo para actualizar',
        value:
          loadingItems
            ? '…'
            : Math.min(results.length || items.length, activeTotal ?? items.length),
        icon: 'Link'
      },
      {
        label:
          'Cambios encontrados',
        value:
          results.length
            ? changedCount
            : '—',
        icon: 'RefreshCw'
      },
      {
        label:
          'Sin cambios',
        value:
          results.length
            ? unchangedCount
            : '—',
        icon: 'CircleCheck'
      },
      {
        label:
          'Requieren revisión',
        value:
          results.length
            ? reviewCount
            : '—',
        icon: 'TriangleAlert'
      }
    ];

  if (!userProfile) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-border bg-card p-6 text-text-secondary">
          Cargando permisos...
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              Actualizador de precios
            </h1>

          </div>

          <div
            className={`rounded-xl border px-4 py-3 ${
              sellerId
                ? 'border-emerald-500/30 bg-emerald-500/10'
                : 'border-amber-500/30 bg-amber-500/10'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  sellerId
                    ? 'bg-emerald-500/15 text-emerald-500'
                    : 'bg-amber-500/15 text-amber-500'
                }`}
              >
                <Icon
                  name={
                    sellerId
                      ? 'Store'
                      : 'TriangleAlert'
                  }
                  size={20}
                />
              </div>

              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
                  Seller activo en este perfil
                </p>

                <p className="mt-0.5 font-semibold text-text-primary">
                  {sellerId || 'No detectado'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-error/20 bg-error/10 p-4 text-sm text-error">
            <Icon
              name="AlertCircle"
              size={20}
            />
            <span>
              {error}
            </span>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(card => (
            <div
              key={card.label}
              className="rounded-xl border border-border bg-card p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">
                    {card.label}
                  </p>

                  <p className="mt-2 text-2xl font-bold text-text-primary">
                    {card.value}
                  </p>
                </div>

                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon
                    name={card.icon}
                    size={21}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border p-5 sm:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">
                  Revisión de precios
                </h2>

              </div>

              {amazonPauseSection === 'prices' && !showPaused && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleScan}
                  disabled={
                    scanning ||
                    loadingItems ||
                    !sellerId ||
                    !mercadoLibreAccessToken
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon
                    name={
                      scanning
                        ? 'Loader2'
                        : 'Search'
                    }
                    size={18}
                    className={
                      scanning
                        ? 'animate-spin'
                        : ''
                    }
                  />

                  {scanning
                    ? `Revisando ${progress}/${items.length}`
                    : 'Buscar cambios de precio'}
                </button>

                <button
                type="button"
                onClick={handlePriceUpdates}
                disabled={
                  applying ||
                  scanning ||
                  !(changedCount > 0) || results.length === 0
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  changedCount > 0
                    ? 'Aplicar los precios calculados'
                    : 'Primero busca cambios de precio'
                }
              >
                <Icon
                  name={
                    applying
                      ? 'LoaderCircle'
                      : 'DollarSign'
                  }
                  size={18}
                />

                {
                  applying
                    ? 'Actualizando...'
                    : 'Actualizar precios'
                }
              </button>
              </div>
              )}
            </div>
          </div>

          <div className="p-5 sm:p-6">
            {loadingItems ? (
              <div className="flex min-h-[220px] items-center justify-center gap-3 text-text-secondary">
                <Icon
                  name="Loader2"
                  size={22}
                  className="animate-spin"
                />
                Cargando productos sincronizados...
              </div>
            ) : items.length === 0 ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center">
                <Icon
                  name="PackageSearch"
                  size={30}
                  className="mb-3 text-text-secondary"
                />

                <h3 className="font-semibold text-text-primary">
                  Aún no hay publicaciones con ASIN listas para recalcular
                </h3>

                <p className="mt-2 max-w-xl text-sm text-text-secondary">
                  El Actualizador solo trabaja con publicaciones que tengan
                  su ASIN registrado en Price Sync.
                </p>
              </div>
            ) : results.length === 0 ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center">
                <Icon
                  name="DollarSign"
                  size={30}
                  className="mb-3 text-primary"
                />

                <h3 className="font-semibold text-text-primary">
                  {items.length} producto{items.length === 1 ? '' : 's'} listo{items.length === 1 ? '' : 's'} para revisar
                </h3>

                <p className="mt-2 max-w-xl text-sm text-text-secondary">
                  Pulsa “Buscar cambios de precio”. El proceso revisará
                  los productos uno por uno para reducir carga y riesgo.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex flex-wrap gap-2 border-b border-border pb-3">
                  <button
                    type="button"
                    onClick={() => setAmazonPauseSection('prices')}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                      amazonPauseSection === 'prices'
                        ? 'border-blue-800 bg-blue-800 text-white'
                        : 'border-border text-text-primary'
                    }`}
                  >
                    Actualizar precios
                  </button>

                  <button
                    type="button"
                    onClick={() => setAmazonPauseSection('pauses')}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                      amazonPauseSection === 'pauses'
                        ? 'border-blue-800 bg-blue-800 text-white'
                        : 'border-border text-text-primary'
                    }`}
                  >
                    Pausas por Amazon ({amazonUnavailableCandidates.length})
                  </button>
                </div>

                {amazonPauseSection === 'prices' ? (
                  <div className="overflow-x-auto">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                  {[
                    ['changes', 'Cambios de precio'],
                    ['unchanged', 'Sin cambios'],
                    ['review', 'Requieren revisión'],
                    ['all', 'Todos']
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPriceFilter(key)}
                      aria-pressed={priceFilter === key}
                      className={
                        'rounded-lg border px-3 py-2 text-sm ' +
                        (priceFilter === key
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-text-secondary')
                      }
                    >
                      {label}
                    </button>
                  ))}

                  
                  <MultiCategoryPicker
                    label="Filtrar categorías de precios"
                    options={priceCategories}
                    value={priceCategoryFilter}
                    onChange={setPriceCategoryFilter}
                  />


                  <select
                    aria-label="Filtrar disponibilidad Amazon"
                    value={priceStockFilter}
                    onChange={e => setPriceStockFilter(e.target.value)}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary"
                  >
                    <option value="all">Toda disponibilidad</option>
                    <option value="out_of_stock">Agotados en Amazon</option>
                    <option value="in_stock">Disponibles en Amazon</option>
                    <option value="unknown">Sin confirmar</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => setSelectedPriceIds([])}
                    className="rounded-lg border border-border px-3 py-2 text-sm text-text-secondary"
                  >
                    Limpiar selección ({selectedPriceIds.length})
                  </button>

                  

                  

                  <input
                    type="search"
                    value={priceSearch}
                    onChange={e => setPriceSearch(e.target.value)}
                    placeholder="Buscar producto, ASIN o publicación"
                    className="min-w-[220px] flex-1 rounded-lg border border-border bg-transparent px-3 py-2 text-sm"
                  />

                  <span className="text-xs text-text-secondary">
                    {filteredPriceResults.length} de {results.length}
                  </span>
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-text-secondary">
                  <strong className="text-text-primary">
                    {selectedPriceIds.length} seleccionados
                  </strong>

                  <span>
                    · {selectedVisibleResults.length} visibles con los filtros
                  </span>

                  <span>
                    · {selectedApplicableCount} con precio para actualizar
                  </span>

                </div>

                <ResultPager page={visiblePricePage}
           total={filteredPriceResults.length}
           size={pricePageSize}
           onPage={setPricePage}
           onSize={size => {
             setPricePageSize(size);
             setPricePage(1);
           }} />
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="border-b border-border text-xs uppercase text-text-secondary">
                    <tr>
                      <th className="px-3 py-3">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar resultados visibles"
                          disabled={
                            applying || scanning ||
                            pagedPriceResults.length === 0
                          }
                          checked={
                            pagedPriceResults.length > 0 &&
                            pagedPriceResults.every(result =>
                              selectedPriceIds.includes(getPriceSyncId(result))
                            )
                          }
                          onChange={e => {
                            const ids = pagedPriceResults
                              .map(getPriceSyncId)
                              .filter(Boolean);

                            setSelectedPriceIds(old =>
                              e.target.checked
                                ? Array.from(new Set([...old, ...ids]))
                                : old.filter(id => !ids.includes(id))
                            );
                          }}
                        />
                      </th>
                      <th className="px-3 py-3">
                        Publicación
                      </th>
                      <th className="px-3 py-3">
                        Producto
                      </th>
                      <th className="px-3 py-3">
                        ASIN
                      </th>
                      <th className="px-3 py-3">
                        Amazon
                      </th>
                      <th className="px-3 py-3">
                        Precio ML actual
                      </th>
                      <th className="px-3 py-3">
                        Precio calculado
                      </th>
                      <th className="px-3 py-3">
                        Diferencia
                      </th>
                      <th className="px-3 py-3">
                        Estado
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-border">
                    {pagedPriceResults.map((result, index) => {
                      const currentMl =
                        result?.mercadoLibre
                          ?.currentPriceCop;

                      const proposedMl =
                        result?.mercadoLibre
                          ?.proposedPriceCop;

                      const difference =
                        result?.mercadoLibre
                          ?.differenceCop;

                      const amazonPrice =
                        result?.amazon
                          ?.currentPriceUsd;

                      return (
                        <tr
                          key={`${result?.item?.ml_item_id}-${index}`}
                          className="align-top"
                        >
                          <td className="px-3 py-4">
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${getPriceSyncId(result)}`}
                              disabled={
                                applying ||
                                scanning ||
                                !getPriceSyncId(result)
                              }
                              checked={
                                selectedPriceIds.includes(getPriceSyncId(result))
                              }
                              onChange={e => {
                                const id = getPriceSyncId(result);

                                setSelectedPriceIds(old =>
                                  e.target.checked
                                    ? Array.from(new Set([...old, id]))
                                    : old.filter(x => x !== id)
                                );
                              }}
                            />
                          </td>
                          <td className="px-3 py-4 font-medium text-text-primary">
                            {result?.item?.ml_item_id || '—'}
                          </td>

                          <td className="px-3 py-4 font-medium text-text-primary">
                            {result?.item?.title || "—"}
                          </td>

                          <td className="px-3 py-4">
                            {result?.item?.asin ? (
                              result?.amazon?.url ? (
                                <a
                                  href={"https://www.amazon.com/dp/" + encodeURIComponent(result.item.asin)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  {result.item.asin}
                                </a>
                              ) : (
                                <span className="text-text-secondary">
                                  {result.item.asin}
                                </span>
                              )
                            ) : (
                              <span className="text-text-secondary">—</span>
                            )}
                          </td>

                          <td className="px-3 py-4">
                            {result.ok
                              ? formatUsd(amazonPrice)
                              : '—'}
                          </td>

                          <td className="px-3 py-4">
                            {result.ok
                              ? formatCop(currentMl)
                              : '—'}
                          </td>

                          <td className="px-3 py-4 font-semibold text-text-primary">
                            {result.ok
                              ? formatCop(proposedMl)
                              : '—'}
                          </td>

                          <td className="px-3 py-4">
                            {result.ok
                              ? formatCop(difference)
                              : '—'}
                          </td>

                          <td className="px-3 py-4">
                            {result.ok ? (
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  result.wouldUpdate
                                    ? 'bg-amber-500/10 text-amber-500'
                                    : 'bg-emerald-500/10 text-emerald-500'
                                }`}
                              >
                                {result.wouldUpdate
                                  ? 'Cambio encontrado'
                                  : 'Sin cambios'}
                              </span>
                            ) : (
                              <div>
                                <span className="inline-flex rounded-full bg-error/10 px-2.5 py-1 text-xs font-semibold text-error">
                                  Revisar
                                </span>

                                <p className="mt-1 max-w-xs text-xs text-error">
                                  {result.error}
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
                  <ResultPager page={visiblePricePage}
           total={filteredPriceResults.length}
           size={pricePageSize}
           onPage={setPricePage}
           onSize={size => {
             setPricePageSize(size);
             setPricePage(1);
           }} />
                  </div>
                ) : (
                  <div className="overflow-x-auto">

                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-text-primary">
                          Productos no disponibles en Amazon
                        </h3>
                        <p className="text-xs text-text-secondary">
                          Solo agotamientos confirmados. La falta de precio no significa agotamiento.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={confirmPricePause}
                        disabled={
                          changingStatus || applying || scanning ||
                          pauseTargets.length === 0
                        }
                        className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
                      >
                        {changingStatus ? (
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                            aria-hidden="true"
                          />
                          Procesando...
                        </span>
                      ) : (
                        `Pausar seleccionados (${pauseTargets.length})`
                      )}
                      </button>
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      
                      <MultiCategoryPicker
                        label="Filtrar categorías de pausas Amazon"
                        options={amazonPauseCategories.map(id => ({
                          id,
                          name: `${id}${priceCategoryNames[id] ? ` — ${priceCategoryNames[id]}` : ''}`
                        }))}
                        value={amazonPauseTabCategory}
                        onChange={setAmazonPauseTabCategory}
                      />


                      <input
                        type="search"
                        value={amazonPauseTabSearch}
                        onChange={e => setAmazonPauseTabSearch(e.target.value)}
                        placeholder="Buscar producto, ASIN o publicación"
                        className="min-w-[200px] flex-1 rounded-lg border border-border px-3 py-2 text-sm"
                      />

                      <button
                        type="button"
                        disabled={changingStatus || applying || scanning}
                        onClick={() => setSelectedAmazonPauseIds(old => [
                          ...new Set([
                            ...old,
                            ...pagedAmazonPauseResults
                              .map(getPriceSyncId)
                              .filter(Boolean)
                          ])
                        ])}
                        className="rounded-lg border border-border px-3 py-2 text-sm"
                      >
                        Seleccionar visibles
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedAmazonPauseIds([])}
                        className="rounded-lg border border-border px-3 py-2 text-sm"
                      >
                        Limpiar selección ({selectedAmazonPauseIds.length})
                      </button>

                      <span className="text-xs text-text-secondary">
                        {amazonPauseTabResults.length} de {amazonUnavailableCandidates.length}
                      </span>
                    </div>

                    <div className="mb-5 overflow-x-auto">
                      <ResultPager page={visibleAmazonPausePage}
           total={amazonPauseTabResults.length}
           size={amazonPausePageSize}
           onPage={setAmazonPausePage}
           onSize={size => {
             setAmazonPausePageSize(size);
             setAmazonPausePage(1);
           }} />
                <table className="w-full min-w-[780px] text-left text-sm">
                        <thead className="border-b border-border text-xs uppercase text-text-secondary">
                          <tr>
                            <th className="p-3">Seleccionar</th>
                            <th className="p-3">Publicación</th>
                            <th className="p-3">Producto</th>
                            <th className="p-3">ASIN</th>
                            <th className="p-3">Categoría</th>
                            <th className="p-3">Amazon</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-border">
                          {pagedAmazonPauseResults.map(r => {
                            const id = getPriceSyncId(r);
                            const cat = String(
                              r?.item?.category_id || ''
                            );

                            return (
                              <tr key={id}>
                                <td className="p-3">
                                  <input
                                    type="checkbox"
                                    aria-label={`Pausar ${id}`}
                                    disabled={changingStatus || applying || scanning}
                                    checked={selectedAmazonPauseIds.includes(id)}
                                    onChange={e => setSelectedAmazonPauseIds(old =>
                                      e.target.checked
                                        ? [...new Set([...old, id])]
                                        : old.filter(x => x !== id)
                                    )}
                                  />
                                </td>

                                <td className="p-3">{id}</td>
                                <td className="p-3">
                                  {r?.item?.title || '—'}
                                </td>
                                <td className="p-3">
                                  {(() => {
                                    const asin = String(
                                      r?.item?.asin || r?.asin || ''
                                    ).trim().toUpperCase();

                                    return /^[A-Z0-9]{10}$/.test(asin) ? (
                                      <a
                                        href={`https://www.amazon.com/dp/${asin}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                        title="Verificar producto en Amazon"
                                      >
                                        {asin}
                                      </a>
                                    ) : '—';
                                  })()}
                                </td>
                                <td className="p-3">
                                  {cat}
                                  {priceCategoryNames[cat]
                                    ? ` — ${priceCategoryNames[cat]}`
                                    : ''}
                                </td>
                                <td className="p-3 font-semibold text-amber-700">
                                  Agotado confirmado
                                </td>
                              </tr>
                            );
                          })}

                          {!amazonPauseTabResults.length && (
                            <tr>
                              <td
                                colSpan={6}
                                className="p-5 text-center text-text-secondary"
                              >
                                No hay productos confirmados como agotados con estos filtros.
                                Ejecuta «Buscar cambios de precio» para consultar Amazon.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                  <ResultPager page={visibleAmazonPausePage}
           total={amazonPauseTabResults.length}
           size={amazonPausePageSize}
           onPage={setAmazonPausePage}
           onSize={size => {
             setAmazonPausePageSize(size);
             setAmazonPausePage(1);
           }} />
                    </div>

                    <div className="mb-3 flex justify-end">
                      <button
                        type="button"
                        disabled={loadingPaused || changingStatus}
                        onClick={() => {
                          if (!showPaused) getPausedItems();
                          setShowPaused(old => !old);
                        }}
                        className="rounded-lg border border-border px-3 py-2 text-sm text-text-primary"
                      >
                        {showPaused
                          ? 'Ocultar pausadas voluntarias'
                          : 'Consultar pausadas voluntarias'}
                      </button>
                    </div>

                    {showPaused && (
                  <div className="mb-4 rounded-lg border border-border p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <strong>
                        Publicaciones pausadas ({pausedVisible.length})
                      </strong>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={getPausedItems}
                          disabled={loadingPaused || changingStatus}
                          className="rounded border border-border px-3 py-2 text-sm disabled:opacity-50"
                        >
                          Recargar
                        </button>

                        <button
                          type="button"
                          onClick={confirmPriceResume}
                          disabled={changingStatus || loadingPaused || !resumeTargets.length}
                          className="rounded bg-blue-800 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                        >
                          Reactivar seleccionados ({resumeTargets.length})
                        </button>
                      </div>
                    </div>

                    <div className="mb-3 flex flex-wrap gap-2">
                      <select
                        value={pausedCategory}
                        onChange={e => setPausedCategory(e.target.value)}
                        className="min-w-[220px] max-w-full rounded border border-border bg-background py-2 pl-3 pr-10 text-sm"
                      >
                        <option value="all">Todas las categorías pausadas</option>
                        {pausedCategories.map(id => (
                          <option key={id} value={id}>
                            {id}{priceCategoryNames[id] ? ` — ${priceCategoryNames[id]}` : ''}
                          </option>
                        ))}
                      </select>

                      <input
                        type="search"
                        value={pausedSearch}
                        onChange={e => setPausedSearch(e.target.value)}
                        placeholder="Buscar pausadas por título, ASIN o ID"
                        className="min-w-[200px] flex-1 rounded border border-border px-3 py-2 text-sm"
                      />

                      <button
                        type="button"
                        disabled={changingStatus}
                        onClick={() =>
                          setSelectedPausedIds(old => [
                            ...new Set([
                              ...old,
                              ...pausedVisible.filter(canResume)
                                .map(r => String(r.ml_item_id))
                            ])
                          ])
                        }
                        className="rounded border border-border px-3 py-2 text-sm disabled:opacity-50"
                      >
                        Seleccionar pausadas visibles
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedPausedIds([])}
                        className="rounded border border-border px-3 py-2 text-sm"
                      >
                        Limpiar selección ({selectedPausedIds.length})
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[670px] text-left text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="p-2">Seleccionar</th>
                            <th className="p-2">Publicación</th>
                            <th className="p-2">Producto</th>
                            <th className="p-2">Categoría</th>
                            <th className="p-2">Motivo</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-border">
                          {pausedVisible.map(r => {
                            const id = String(r.ml_item_id);

                            return (
                              <tr key={id}>
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    disabled={!canResume(r) || changingStatus}
                                    checked={selectedPausedIds.includes(id)}
                                    onChange={e =>
                                      setSelectedPausedIds(old =>
                                        e.target.checked
                                          ? [...new Set([...old, id])]
                                          : old.filter(x => x !== id)
                                      )
                                    }
                                  />
                                </td>

                                <td className="p-2">{id}</td>
                                <td className="p-2">{r.title || '—'}</td>
                                <td className="p-2">
                                  {r.category_id || '—'}
                                  {priceCategoryNames[r.category_id]
                                    ? ` — ${priceCategoryNames[r.category_id]}`
                                    : ''}
                                </td>
                                <td className="p-2">
                                  {canResume(r)
                                    ? 'Pausada por vendedor'
                                    : (r.sub_status || []).join(', ') ||
                                      'Requiere revisión'}
                                </td>
                              </tr>
                            );
                          })}

                          {!pausedVisible.length && (
                            <tr>
                              <td
                                colSpan={5}
                                className="p-4 text-center text-text-secondary"
                              >
                                {loadingPaused
                                  ? 'Consultando...'
                                  : 'No hay pausadas con estos filtros.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PriceUpdater;
